var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, LitElement } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { unsafeSVG } from "lit/directives/unsafe-svg.js";
// import { invokeCoreApi } from "../util/coreApiClient"
import { env } from "../auth/config/env";
import { styles } from "../styles";
import { BenderLocalizationLoader } from "../util/bender-translations";
import { icons } from "../util/icons";
import { getApplications } from "./apps-details";
/**
 * The App Switch consists of buttons for each linked application. By clicking on a button, the user is redirected to the respective application, and the application-related button is highlighted to indicate the current application.
 */
let AppSwitch = class AppSwitch extends LitElement {
    constructor() {
        super(...arguments);
        this.userRights = [];
        this.tenantLicenseFeatures = [];
        this.showDropdown = false;
        this.localizationLoader = new BenderLocalizationLoader();
        this.currentUrl = window.location.href;
        this.storeUserRights = async () => {
            var _a;
            const account = window.benderAuth.getAccount();
            this.userRights = (_a = account.rights) !== null && _a !== void 0 ? _a : [];
        };
        this.storeTenantLicenses = async () => {
            const account = window.benderAuth.getAccount();
            this.tenantLicenseFeatures = account.licenseFeatures
                ? Object.entries(account.licenseFeatures)
                    .filter(([_, value]) => value === 1)
                    .map(([key, _]) => key)
                : [];
        };
    }
    render() {
        return html `
      <div class="app-switch-container">
        <button
          class="header-btn"
          @click=${() => {
            this.toggleDropdown();
        }}
        >
          ${unsafeSVG(icons.appswitch)}
        </button>
        <div
          ?hidden=${!this.showDropdown}
          class="dropdown-content"
        >
          <div class="switch-header">Apps</div>
          <div class="switch-container">
            ${this.getApplications()
            .map(app => this.renderApp(app))
            .filter(template => !!template)}
          </div>
          <div class="switch-footer">
            <a
              href="${this.buildFrontDoorUrl()}"
              class="dropdown-item"
            >
              <div class="icon-text-container">
                <div class="footer-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M13.29 17.71 7.59 12l5.7-5.71 1.42 1.42-4.3 4.29 4.3 4.29-1.42 1.42z"
                      fill="#3a3a3a"
                    />
                  </svg>
                </div>
                Bender Connect Home
              </div>
            </a>
          </div>
        </div>
      </div>
      <div
        ?hidden=${!this.showDropdown}
        @click=${() => {
            this.showDropdown = false;
        }}
        class="dropdown-background"
      ></div>
    `;
    }
    getApplications() {
        if (!this.translator) {
            return [];
        }
        return getApplications(this.translator);
    }
    connectedCallback() {
        super.connectedCallback();
        void this.localizationLoader.loadLocalizations().then(localizationServiceAdapter => {
            this.translator = localizationServiceAdapter;
            this.requestUpdate();
        });
        const bindedCheckHasAccount = this.checkHasAccount.bind(this);
        this.intervalLoginCheck = setInterval(bindedCheckHasAccount, 1000);
        this.storeUserRights();
        this.storeTenantLicenses();
    }
    checkHasAccount() {
        if (window.benderAuth.hasAccounts) {
            clearInterval(this.intervalLoginCheck);
            this.storeUserRights();
            this.storeTenantLicenses();
        }
    }
    disconnectedCallback() {
        super.disconnectedCallback();
    }
    toggleDropdown() {
        this.showDropdown = !this.showDropdown;
    }
    renderApp(app) {
        var _a;
        const hasAccess = this.userHasOneRequiredRight((_a = app.requiredRights) !== null && _a !== void 0 ? _a : []) && this.tenantHasLicenseFeature(app.requiredLicenseFeature);
        if (!hasAccess && !app.showEvenIfDisabled) {
            return undefined;
        }
        const classes = {
            disabled: !hasAccess
        };
        const url = this.buildUrlWithTenantId(app.url);
        return html `<a
      href="${url}"
      class="app-link ${classMap(classes)}"
    >
      <div>${unsafeSVG(app.icon)}</div>
      <div>${app.displayName}</div>
    </a> `;
    }
    userHasOneRequiredRight(rights) {
        if (rights.length) {
            return rights.some(r => { var _a, _b; return (_b = (_a = this.userRights) === null || _a === void 0 ? void 0 : _a.includes(r)) !== null && _b !== void 0 ? _b : false; });
        }
        return true;
    }
    tenantHasLicenseFeature(feature) {
        var _a;
        return !feature || !!((_a = this.tenantLicenseFeatures) === null || _a === void 0 ? void 0 : _a.includes(feature));
    }
    /**
     * Builds a URL with the tenant ID parameter if available.
     * Handles existing query parameters by using & separator when needed.
     */
    buildUrlWithTenantId(baseUrl) {
        const account = window.benderAuth.getAccount();
        const tenantId = account === null || account === void 0 ? void 0 : account.currentTenantId;
        if (!tenantId) {
            return baseUrl;
        }
        const separator = baseUrl.includes("?") ? "&" : "?";
        return `${baseUrl}${separator}tenantId=${tenantId}`;
    }
    buildFrontDoorUrl() {
        return this.buildUrlWithTenantId(env.frontDoorUrl);
    }
};
AppSwitch.styles = styles;
__decorate([
    property({ type: Array })
], AppSwitch.prototype, "userRights", void 0);
__decorate([
    property({ type: Array })
], AppSwitch.prototype, "tenantLicenseFeatures", void 0);
__decorate([
    state()
], AppSwitch.prototype, "showDropdown", void 0);
__decorate([
    state()
], AppSwitch.prototype, "translator", void 0);
AppSwitch = __decorate([
    customElement("bender-app-switch")
], AppSwitch);
export { AppSwitch };
//# sourceMappingURL=app-switch.js.map